import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Flame, CookingPot, Microwave, Soup, Sandwich, Search, X } from "lucide-react";
import { useProducts, useCategories } from "@/lib/api";
import Seo from "@/components/common/Seo";
import { cn } from "@/lib/utils";
import SectionHeading from "@/components/common/SectionHeading";
import SmartGuide from "@/components/products/SmartGuide";
import ProductCard from "@/components/products/ProductCard";

const METHODS = ["Mangal", "Tava", "Fırın", "Tencere"];
const METHOD_ICONS = { Mangal: Flame, Tava: CookingPot, Fırın: Microwave, Tencere: Soup, Burger: Sandwich };

export default function Products() {
  const { data: products = [] } = useProducts();
  const { data: categories = [] } = useCategories();
  const [searchParams] = useSearchParams();
  const initialMethod = (() => {
    const m = searchParams.get("method");
    if (m && (METHODS.includes(m) || m === "Burger")) return m;
    return null;
  })();
  const [category, setCategory] = useState("Tümü");
  const [method, setMethod] = useState(initialMethod);
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(24);

  useEffect(() => {
    setVisible(24);
  }, [category, method, query]);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("tr");
    return products.filter((p) => {
      const catOk = category === "Tümü" || p.category === category;
      let methodOk = true;
      if (method === "Burger") methodOk = ["burger-koftesi", "kasap-kofte", "dana-kiyma"].includes(p.slug);
      else if (method) methodOk = p.cooking_methods?.includes(method);
      const searchOk =
        !q ||
        p.name.toLocaleLowerCase("tr").includes(q) ||
        p.category.toLocaleLowerCase("tr").includes(q) ||
        (p.short_description || "").toLocaleLowerCase("tr").includes(q);
      return catOk && methodOk && searchOk;
    });
  }, [products, category, method, query]);

  const cats = ["Tümü", ...categories.map((c) => c.name)];

  const pill = (active) =>
    cn(
      "inline-flex min-h-[44px] items-center rounded-sm border px-4 font-mono text-xs uppercase tracking-wide transition-colors",
      active ? "border-bordo bg-bordo text-cream" : "border-dashed border-charcoal/30 text-charcoal/70 hover:border-bordo hover:text-bordo"
    );

  return (
    <div data-testid="products-page" className="bg-cream">
      <Seo
        title="Ürünler"
        description="Dana, kuzu, tavuk, köfte, şarküteri ve premium kesimler. 36 özenle seçilmiş ürün — lezzet, yumuşaklık ve pişirme notlarıyla. Doğru eti birlikte seçelim."
        jsonLd={products.length ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Ertan Kasap Ürünleri",
          numberOfItems: products.length,
          itemListElement: products.slice(0, 40).map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: (process.env.REACT_APP_SITE_URL || window.location.origin) + "/urun/" + p.slug,
            name: p.name,
          })),
        } : undefined}
      />
      <section className="border-b border-dashed border-bordo/30 pt-16 pb-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading as="h1" eyebrow="Ürün Kataloğu" title="Doğru Eti Birlikte Seçelim" description="Akıllı rehberle size özel öneriler alın ya da kategorilere göz atın." />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <SmartGuide products={products} />
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="mb-8 border-t border-dashed border-bordo/30 pt-8">
          <div className="relative mb-6 max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-charcoal/40" />
            <input
              data-testid="product-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ürün ara… (örn. antrikot, pirzola, sucuk)"
              className="w-full rounded-sm border border-dashed border-charcoal/30 bg-paper py-3 pl-10 pr-10 font-mono text-sm text-charcoal placeholder:text-charcoal/35 focus:border-bordo focus:outline-none"
            />
            {query && (
              <button
                data-testid="product-search-clear"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-charcoal/40 transition-colors hover:text-bordo"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <span className="mb-3 block font-mono text-xs uppercase tracking-[0.2em] text-charcoal/50">Kategoriler</span>
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => (
              <button key={c} data-testid={`filter-category-${c}`} onClick={() => setCategory(c)} className={pill(category === c)}>
                {c}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs uppercase tracking-wider text-charcoal/40">Pişirme:</span>
            <button onClick={() => setMethod(null)} data-testid="filter-method-all" className={pill(!method)}>Tümü</button>
            {[...METHODS, "Burger"].map((m) => {
              const Icon = METHOD_ICONS[m] || Flame;
              return (
                <button key={m} data-testid={`filter-method-${m}`} onClick={() => setMethod(m === method ? null : m)} className={cn(pill(method === m), "inline-flex items-center gap-1.5")}>
                  <Icon className="h-3.5 w-3.5" /> {m}
                </button>
              );
            })}
          </div>
        </div>

        <p className="mb-6 font-mono text-xs uppercase tracking-wider text-charcoal/50" data-testid="products-count">{filtered.length} ürün listeleniyor</p>

        {filtered.length === 0 ? (
          <div className="border border-dashed border-bordo/30 py-20 text-center font-mono text-sm text-charcoal/50">
            Bu filtrelere uygun ürün bulunamadı. Filtreleri değiştirmeyi deneyin.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.slice(0, visible).map((p) => <ProductCard key={p.slug} product={p} />)}
            </div>
            {visible < filtered.length && (
              <div className="mt-12 text-center">
                <button
                  data-testid="products-load-more"
                  onClick={() => setVisible((v) => v + 24)}
                  className="inline-flex items-center justify-center rounded-sm border border-bordo bg-transparent px-8 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-bordo transition-colors hover:bg-bordo hover:text-cream"
                >
                  Daha Fazla Göster ({filtered.length - visible})
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
